// CRM-Import: reine Matching-/Clustering-Logik (Phase 2).
// KEINE Supabase-, KEINE React-Abhaengigkeiten -> ohne jsdom mit vitest testbar.
// Aufbau wie checklistLogic.ts: pure Funktionen, deterministisch, gut testbar.

import type { ReportMetadata } from '../types';

// ─── Normalisierung ──────────────────────────────────────────────────────────

// Deutsche Rechtsformen in bereits interpunktionsfreier Form (kleinbuchstaben),
// laengste zuerst, damit "gmbh & co. kg" nicht als blosses "kg"/"gmbh" greift.
const LEGAL_FORMS: string[] = [
  'gmbh und co kg',
  'gmbh co kg',
  'ug haftungsbeschraenkt',
  'gmbh',
  'mbh',
  'ohg',
  'gbr',
  'e k',
  'ek',
  'ag',
  'ug',
  'kg',
];

const replaceUmlauts = (s: string): string =>
  s
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss');

/**
 * Normalisiert einen Firmennamen fuer den Vergleich:
 * lowercase, Umlaute (ae/oe/ue/ss), Interpunktion -> Leerzeichen,
 * Whitespace kollabiert, deutsche Rechtsform-Suffixe entfernt (laengste zuerst).
 */
export const normalizeCompanyName = (name: string): string => {
  if (!name) return '';
  let s = replaceUmlauts(name.toLowerCase());
  // Interpunktion/Sonderzeichen -> Leerzeichen (Ziffern & Buchstaben bleiben).
  s = s.replace(/[^a-z0-9\s]/g, ' ');
  s = s.replace(/\s+/g, ' ').trim();
  if (!s) return '';

  // Rechtsform-Suffix am Ende entfernen (nur der laengste Treffer).
  for (const form of LEGAL_FORMS) {
    if (s === form) {
      // Reiner Rechtsform-Name -> nichts sinnvolles uebrig, Original-Tokens behalten.
      break;
    }
    if (s.endsWith(' ' + form)) {
      s = s.slice(0, s.length - form.length - 1).trim();
      break;
    }
  }

  return s.replace(/\s+/g, ' ').trim();
};

// ─── Levenshtein ─────────────────────────────────────────────────────────────

/** Levenshtein-Distanz (kleiner interner Helfer, fuer Tests exportiert). */
export const levenshtein = (a: string, b: string): number => {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  let prev = new Array(b.length + 1);
  let curr = new Array(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;

  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        prev[j] + 1, // Loeschung
        curr[j - 1] + 1, // Einfuegung
        prev[j - 1] + cost // Ersetzung
      );
    }
    [prev, curr] = [curr, prev];
  }
  return prev[b.length];
};

// ─── Aehnlichkeit ────────────────────────────────────────────────────────────

const normalizeAddress = (addr?: string): string => {
  if (!addr) return '';
  return replaceUmlauts(addr.toLowerCase())
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const tokenJaccard = (a: string, b: string): number => {
  const ta = new Set(a.split(' ').filter(Boolean));
  const tb = new Set(b.split(' ').filter(Boolean));
  if (ta.size === 0 && tb.size === 0) return 1;
  if (ta.size === 0 || tb.size === 0) return 0;
  let inter = 0;
  for (const t of ta) if (tb.has(t)) inter++;
  const union = ta.size + tb.size - inter;
  return union === 0 ? 0 : inter / union;
};

const levRatio = (a: string, b: string): number => {
  const maxLen = Math.max(a.length, b.length);
  if (maxLen === 0) return 1;
  return 1 - levenshtein(a, b) / maxLen;
};

/**
 * Aehnlichkeit zweier Kunden 0..1: Token-Jaccard des normalisierten Namens
 * kombiniert (max) mit der normalisierten Levenshtein-Ratio, plus Bonus, wenn
 * die normalisierten Adressen gleich sind oder eine die andere enthaelt.
 */
export const customerSimilarity = (
  a: { name: string; address?: string },
  b: { name: string; address?: string }
): number => {
  const na = normalizeCompanyName(a.name);
  const nb = normalizeCompanyName(b.name);
  if (!na && !nb) return 0;

  const nameScore = Math.max(tokenJaccard(na, nb), levRatio(na, nb));

  let bonus = 0;
  const aa = normalizeAddress(a.address);
  const ab = normalizeAddress(b.address);
  if (aa && ab && (aa === ab || aa.includes(ab) || ab.includes(aa))) {
    bonus = 0.15;
  }

  return Math.min(1, nameScore + bonus);
};

// ─── Kandidaten-Extraktion ───────────────────────────────────────────────────

/**
 * Rolle, aus der der Name stammt. 'bauherr' heisst: das Projekt fuehrt gar
 * keinen Auftraggeber, gelesen wurde nur der Bauherr. Beides ist NICHT
 * dasselbe — der Auftraggeber traegt die Rechnung, der Bauherr muss sie nie
 * bekommen. Der Aufrufer muss die Rolle sichtbar machen und darf einen
 * Bauherrn nicht ungefragt zum Kunden des Projekts machen.
 */
export type CandidateRole = 'auftraggeber' | 'bauherr';

export interface CustomerCandidate {
  key: string;
  name: string;
  address?: string;
  contactPerson?: string;
  phone?: string;
  email?: string;
  /**
   * Projekte, in denen dieser Name der AUFTRAGGEBER ist — nur die duerfen
   * beim Import auf den Kunden verknuepft werden.
   */
  projectIds: string[];
  /**
   * Projekte, in denen der Name nur als Bauherr steht. Bewusst getrennt: der
   * Kunde darf daraus entstehen, die Rechnungsbeziehung (customer_id) NICHT.
   */
  clientOnlyProjectIds: string[];
  /** 'auftraggeber', sobald der Name in mindestens einem Projekt Auftraggeber ist. */
  role: CandidateRole;
  variants: { name: string; address?: string; projectId: string; role: CandidateRole }[];
}

const str = (v: any): string => (typeof v === 'string' ? v.trim() : '');

const completeness = (v: {
  name: string;
  address?: string;
  contactPerson?: string;
  phone?: string;
  email?: string;
}): number =>
  (v.name ? 1 : 0) +
  (v.address ? 1 : 0) +
  (v.contactPerson ? 1 : 0) +
  (v.phone ? 1 : 0) +
  (v.email ? 1 : 0);

/**
 * Was extractCustomerCandidates an einer Projektzeile liest — strukturell statt
 * ListProject, damit Teilobjekte (Tests, Rohzeilen) passen. Ein Eintrag der
 * Projektliste erfuellt die Form; Rohzeilen mit der Spalte `metadata` (ohne
 * den Umbau zu `data.metadata`) ebenso.
 */
export interface CustomerCandidateSource {
  id?: string | null;
  customer_id?: string | null;
  data?: { metadata?: Partial<ReportMetadata> | null } | null;
  metadata?: Partial<ReportMetadata> | null;
}

/**
 * Extrahiert Kunden-Kandidaten aus Projekt-Rows. Liest row.data.metadata
 * (customer*-Felder, ersatzweise die client*-Felder wenn customerName leer),
 * ueberspringt Rows ohne Namen und bereits verknuepfte Rows (row.customer_id),
 * gruppiert nach normalizeCompanyName und fuehrt Projekte + variants zusammen.
 *
 * Der Ersatz aus den client*-Feldern wird NICHT zum Auftraggeber erklaert: er
 * landet mit role 'bauherr' in clientOnlyProjectIds und bleibt damit aus der
 * Verknuepfung heraus. Als Firma darf er trotzdem angelegt werden — nur die
 * Zuordnung zum Projekt ist eine Aussage, die der Import nicht treffen kann.
 */
export const extractCustomerCandidates = (
  projects: readonly (CustomerCandidateSource | null | undefined)[],
): CustomerCandidate[] => {
  const byKey = new Map<
    string,
    CustomerCandidate & {
      _best: { fields: number; nameLen: number };
    }
  >();

  for (const row of projects || []) {
    if (!row) continue;
    if (row.customer_id) continue; // schon einem Kunden zugeordnet -> ignorieren

    const meta: Partial<ReportMetadata> = row.data?.metadata ?? row.metadata ?? {};

    let name = str(meta.customerName);
    let address = str(meta.customerAddress);
    let contactPerson = str(meta.customerContactPerson);
    let phone = str(meta.customerPhone);
    let email = str(meta.customerEmail);

    let role: CandidateRole = 'auftraggeber';
    if (!name) {
      role = 'bauherr';
      name = str(meta.clientName);
      address = address || str(meta.clientAddress);
      contactPerson = contactPerson || str(meta.clientContactPerson);
      phone = phone || str(meta.clientPhone);
      email = email || str(meta.clientEmail);
    }

    if (!name) continue; // leere/whitespace-Namen ueberspringen

    const key = normalizeCompanyName(name);
    if (!key) continue;

    const projectId = String(row.id ?? '');
    const variantFields = { name, address, contactPerson, phone, email };
    const fields = completeness(variantFields);

    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, {
        key,
        name,
        address: address || undefined,
        contactPerson: contactPerson || undefined,
        phone: phone || undefined,
        email: email || undefined,
        projectIds: projectId && role === 'auftraggeber' ? [projectId] : [],
        clientOnlyProjectIds: projectId && role === 'bauherr' ? [projectId] : [],
        role,
        variants: [{ name, address: address || undefined, projectId, role }],
        _best: { fields, nameLen: name.length },
      });
    } else {
      const bucket = role === 'auftraggeber' ? existing.projectIds : existing.clientOnlyProjectIds;
      if (projectId && !bucket.includes(projectId)) bucket.push(projectId);
      // Eine einzige Auftraggeber-Fundstelle genuegt, damit die Firma als
      // Kunde gilt; die Bauherr-Projekte bleiben trotzdem unverknuepft.
      if (role === 'auftraggeber') existing.role = 'auftraggeber';
      existing.variants.push({ name, address: address || undefined, projectId, role });
      // Vollstaendigste Variante gewinnt die Anzeigefelder (Tiebreak: laengerer Name).
      if (
        fields > existing._best.fields ||
        (fields === existing._best.fields && name.length > existing._best.nameLen)
      ) {
        existing.name = name;
        existing.address = address || undefined;
        existing.contactPerson = contactPerson || undefined;
        existing.phone = phone || undefined;
        existing.email = email || undefined;
        existing._best = { fields, nameLen: name.length };
      }
    }
  }

  // _best-Hilfsfeld entfernen.
  return Array.from(byKey.values()).map(({ _best, ...c }) => c);
};

// ─── Clustering ──────────────────────────────────────────────────────────────

/**
 * Single-Linkage-Clustering ueber customerSimilarity >= threshold: ein Kandidat
 * tritt einem Cluster bei, sobald er zu IRGENDEINEM (nicht nur dem ersten)
 * Mitglied aehnlich genug ist. Dadurch landen transitive Duplikate (A~B, B~C,
 * aber A!~C) unabhaengig von der Eingabereihenfolge in einer Gruppe.
 * Einzel-Cluster -> unique, Mehrfach-Cluster -> groups (Nutzerentscheidung noetig).
 */
export const clusterCandidates = (
  candidates: CustomerCandidate[],
  threshold = 0.85
): { unique: CustomerCandidate[]; groups: CustomerCandidate[][] } => {
  const clusters: CustomerCandidate[][] = [];
  const assigned = new Set<number>();

  for (let i = 0; i < candidates.length; i++) {
    if (assigned.has(i)) continue;
    const cluster = [candidates[i]];
    assigned.add(i);
    // Solange der Cluster waechst, erneut alle freien Kandidaten pruefen: ein
    // neu aufgenommenes Mitglied kann weitere (transitiv verbundene) anziehen.
    let grew = true;
    while (grew) {
      grew = false;
      for (let j = 0; j < candidates.length; j++) {
        if (assigned.has(j)) continue;
        if (cluster.some((m) => customerSimilarity(m, candidates[j]) >= threshold)) {
          cluster.push(candidates[j]);
          assigned.add(j);
          grew = true;
        }
      }
    }
    clusters.push(cluster);
  }

  const unique: CustomerCandidate[] = [];
  const groups: CustomerCandidate[][] = [];
  for (const c of clusters) {
    if (c.length === 1) unique.push(c[0]);
    else groups.push(c);
  }
  return { unique, groups };
};

// ─── Abgleich mit bestehenden Kunden ─────────────────────────────────────────

/**
 * Bester bestehender Kunde ueber threshold, sonst null.
 */
export const matchAgainstExisting = (
  candidate: { name: string; address?: string },
  existing: { id: string; name: string; address?: string }[],
  threshold = 0.85
): { id: string; name: string } | null => {
  let best: { id: string; name: string } | null = null;
  let bestScore = threshold;
  for (const e of existing || []) {
    const score = customerSimilarity(candidate, e);
    if (score >= bestScore) {
      bestScore = score;
      best = { id: e.id, name: e.name };
    }
  }
  return best;
};
